import { ClassNames } from '@emotion/react'
import styled from '@emotion/styled'
import { Dialog as HeadlessUiDialog, Transition } from '@headlessui/react'
import { FC, Fragment, ReactNode, RefObject } from 'react'

import { DialogProvider, useDialogContext } from './DialogProvider'
import { Color, theme } from '../../constants'
import { StyleProps, createStyles } from '../../utils'
import { Optional } from '../../utils/types'
import { Icon, IconProps } from '../Icon'

type InitialFocusRef = RefObject<any>

type DialogProps = {
  children: ReactNode
  initialFocus?: InitialFocusRef
  isOpen: boolean
  onClose: () => void
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
`

const Content = styled.div`
  position: fixed;
  inset: 0;
  display: grid;
  align-items: center;
  justify-items: center;
  padding: 1rem;
  overflow: auto;
`

const DialogComponent = ({
  isOpen,
  onClose,
  initialFocus,
  children,
}: DialogProps) => {
  return (
    <ClassNames>
      {({ css }) => (
        <Transition show={isOpen} as={Fragment}>
          <HeadlessUiDialog
            onClose={onClose}
            className={css`
              position: relative;
              z-index: ${theme.zIndex.popover};
            `}
            initialFocus={initialFocus}
          >
            <Transition.Child
              as={Fragment}
              enter={css`
                transition: opacity ${theme.animation.transition};
              `}
              enterFrom={css`
                opacity: 0;
              `}
              enterTo={css`
                opacity: 100%;
              `}
              leave={css`
                transition: opacity ${theme.animation.transition};
              `}
              leaveFrom={css`
                opacity: 100%;
              `}
              leaveTo={css`
                opacity: 0;
              `}
            >
              <Backdrop />
            </Transition.Child>

            <Transition.Child
              as={Fragment}
              enter={css`
                transition:
                  opacity ${theme.animation.transition},
                  transform ${theme.animation.transition};
              `}
              enterFrom={css`
                opacity: 0;
                transform: scale(0.95);
              `}
              enterTo={css`
                opacity: 100%;
                transform: scale(1);
              `}
              leave={css`
                transition:
                  opacity ${theme.animation.transition},
                  transform ${theme.animation.transition};
              `}
              leaveFrom={css`
                opacity: 100%;
                transform: scale(1);
              `}
              leaveTo={css`
                opacity: 0;
                transform: scale(0.95);
              `}
            >
              <Content>
                <DialogProvider isOpen={isOpen} onClose={onClose}>
                  {children}
                </DialogProvider>
              </Content>
            </Transition.Child>
          </HeadlessUiDialog>
        </Transition>
      )}
    </ClassNames>
  )
}

type ChildComponentProps = {
  children?: ReactNode
  className?: string
  styles?: StyleProps
}

const Panel: FC<ChildComponentProps> = styled(
  HeadlessUiDialog.Panel,
)<ChildComponentProps>((props) => createStyles(props.styles))

const Title: FC<ChildComponentProps> = styled(
  HeadlessUiDialog.Title,
)<ChildComponentProps>((props) => createStyles(props.styles))

const Description: FC<ChildComponentProps> = styled(
  HeadlessUiDialog.Description,
)<ChildComponentProps>((props) => createStyles(props.styles))

const CloseButtonElement = styled.button`
  background: transparent;
  text-align: center;
  box-sizing: border-box;
  cursor: pointer;
  border: none;
  padding: 0;
  color: ${Color.DARK_GREY};

  :hover {
    color: ${Color.DARKER_GREY};
  }

  :active {
    color: ${Color.CHARCOAL};
  }
`

const Close = function Close({
  className,
  onClick,
  ...iconProps
}: Optional<IconProps, 'name'>) {
  const { onClose } = useDialogContext()

  return (
    <CloseButtonElement
      type="button"
      className={className}
      onClick={onClick || onClose}
    >
      <Icon name="XIcon" size="lg" {...iconProps} />
    </CloseButtonElement>
  )
}

export const Dialog = Object.assign(DialogComponent, {
  Panel,
  Title,
  Description,
  Close,
})

export type { DialogProps }
